import { useState, useMemo } from 'react';
import {
  blockchainAudit,
  type AuditTransaction,
  type AuditTransactionType,
  type ChainState,
} from '@/services/security/blockchainAudit';
import {
  accessControl,
  type AccessPolicy,
  type SecurityIncident,
  type PolicyType,
} from '@/services/security/accessControl';
import styles from './SecurityAuditPage.module.css';

type TabType = 'audit' | 'policies' | 'incidents';

// Mock data generators
const generateMockAuditTransactions = (): AuditTransaction[] => {
  const types: AuditTransactionType[] = [
    'pfile_view', 'pfile_update', 'document_download', 'document_upload',
    'checkout', 'checkin', 'login', 'logout', 'search_execute', 'failed_access'
  ];
  const users = [
    { id: 'user1', name: 'Kwame Asante', role: 'Records Officer', mda: 'Ministry of Education' },
    { id: 'user2', name: 'Akosua Mensah', role: 'HR Manager', mda: 'Ministry of Health' },
    { id: 'user3', name: 'Kofi Owusu', role: 'Admin', mda: 'OHCS' },
    { id: 'user4', name: 'Ama Serwaa', role: 'Supervisor', mda: 'Ministry of Finance' },
    { id: 'user5', name: 'Yaw Boateng', role: 'Records Clerk', mda: 'Ministry of Education' },
  ];
  const subjects = [
    { id: 'pf001', identifier: 'GCS-2024-001234', name: 'John K. Mensah' },
    { id: 'pf002', identifier: 'GCS-2024-001235', name: 'Mary A. Owusu' },
    { id: 'pf003', identifier: 'GCS-2024-001236', name: 'Samuel T. Asare' },
    { id: 'doc001', identifier: 'DOC-2024-0001', name: 'Appointment Letter' },
    { id: 'doc002', identifier: 'DOC-2024-0002', name: 'Promotion Letter' },
  ];

  const transactions: AuditTransaction[] = [];
  for (let i = 0; i < 50; i++) {
    const type = types[Math.floor(Math.random() * types.length)];
    const user = users[Math.floor(Math.random() * users.length)];
    const subject = subjects[Math.floor(Math.random() * subjects.length)];
    const daysAgo = Math.floor(Math.random() * 30);
    const timestamp = new Date();
    timestamp.setDate(timestamp.getDate() - daysAgo);
    timestamp.setHours(Math.floor(Math.random() * 10) + 8);
    timestamp.setMinutes(Math.floor(Math.random() * 60));

    transactions.push({
      transactionId: `txn_${Date.now()}_${i}`,
      timestamp,
      type,
      actor: {
        userId: user.id,
        username: user.name.toLowerCase().replace(' ', '.'),
        name: user.name,
        role: user.role,
        mda: user.mda,
        sessionId: `sess_${Math.random().toString(36).slice(2)}`,
      },
      subject: {
        type: subject.id.startsWith('pf') ? 'pfile' : 'document',
        id: subject.id,
        identifier: subject.identifier,
        name: subject.name,
      },
      action: blockchainAudit.getTransactionTypeLabel(type),
      details: { browser: 'Chrome 120', os: 'Windows 11' },
      ipAddress: `192.168.1.${Math.floor(Math.random() * 255)}`,
      signature: `SIG_${Math.random().toString(36).slice(2, 18)}`,
      verified: true,
    });
  }
  return transactions.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
};

const generateMockChainState = (): ChainState => ({
  chainId: 'ohcs-audit-chain-001',
  genesisBlock: 'genesis_block_hash_2024',
  currentBlockNumber: 1247,
  currentBlockHash: '00a3f7b2c1d4e5f6g7h8i9j0k1l2m3n4o5p6q7r8s9t0',
  totalTransactions: 15843,
  lastValidatedAt: new Date(),
  isValid: true,
  syncStatus: 'synced',
  peers: ['peer-accra-01', 'peer-kumasi-01', 'peer-tamale-01'],
});

const generateMockPolicies = (): AccessPolicy[] => [
  {
    id: 'pol_001',
    name: 'Standard Office Hours Access',
    description: 'Allow access to internal records during working hours (8AM-5PM, Mon-Fri)',
    type: 'context_based',
    priority: 10,
    isActive: true,
    scope: { sensitivityLevels: ['internal', 'public'] },
    conditions: [
      { id: 'c1', type: 'time_of_day', operator: 'between', value: [8, 17], isRequired: true },
      { id: 'c2', type: 'day_of_week', operator: 'between', value: [1, 5], isRequired: true },
    ],
    actions: [{ type: 'allow', scope: 'all' }],
    createdAt: new Date('2024-01-15'),
    createdBy: 'System Admin',
    updatedAt: new Date('2024-06-20'),
  },
  {
    id: 'pol_002',
    name: 'Confidential Records Access',
    description: 'Require MFA for confidential and secret documents',
    type: 'attribute_based',
    priority: 5,
    isActive: true,
    scope: { sensitivityLevels: ['confidential', 'secret'] },
    conditions: [
      { id: 'c3', type: 'authentication_method', operator: 'in', value: ['password_mfa', 'biometric', 'smart_card'], isRequired: true },
      { id: 'c4', type: 'clearance_level', operator: 'greater_than', value: 2, isRequired: true },
    ],
    actions: [{ type: 'allow', scope: 'view', restrictions: { watermark: true, preventPrint: true } }],
    createdAt: new Date('2024-02-10'),
    createdBy: 'Security Admin',
    updatedAt: new Date('2024-07-15'),
  },
  {
    id: 'pol_003',
    name: 'Geofence - Ministries Only',
    description: 'Restrict access to sensitive operations within government premises',
    type: 'context_based',
    priority: 3,
    isActive: true,
    scope: { sensitivityLevels: ['secret', 'top_secret'] },
    conditions: [
      { id: 'c5', type: 'location', operator: 'in', value: 'gf_ministries', isRequired: true },
    ],
    actions: [{ type: 'deny', scope: 'all' }],
    createdAt: new Date('2024-03-01'),
    createdBy: 'Security Admin',
    updatedAt: new Date('2024-03-01'),
  },
  {
    id: 'pol_004',
    name: 'Emergency Access Override',
    description: 'Allow supervisors emergency access with full audit trail',
    type: 'emergency',
    priority: 1,
    isActive: true,
    scope: {},
    conditions: [
      { id: 'c6', type: 'role', operator: 'in', value: ['Supervisor', 'Director', 'Admin'], isRequired: true },
      { id: 'c7', type: 'purpose', operator: 'equals', value: 'emergency', isRequired: true },
    ],
    actions: [{ type: 'allow', scope: 'all', restrictions: { expiresAfter: 60 } }, { type: 'alert', scope: 'all' }],
    createdAt: new Date('2024-01-01'),
    createdBy: 'System Admin',
    updatedAt: new Date('2024-01-01'),
  },
  {
    id: 'pol_005',
    name: 'Cross-MDA Transfer Approval',
    description: 'Require approval for inter-MDA record transfers',
    type: 'role_based',
    priority: 8,
    isActive: true,
    scope: { recordTypes: ['transfer'] },
    conditions: [
      { id: 'c8', type: 'role', operator: 'not_in', value: ['Director', 'Admin'], isRequired: true },
    ],
    actions: [{ type: 'require_approval', scope: 'all' }],
    createdAt: new Date('2024-04-15'),
    createdBy: 'Policy Admin',
    updatedAt: new Date('2024-08-01'),
  },
];

const generateMockIncidents = (): SecurityIncident[] => [
  {
    id: 'inc_001',
    type: 'failed_authentication',
    severity: 'medium',
    timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000),
    userId: 'user_unknown',
    userName: 'Unknown',
    description: 'Multiple failed login attempts (5) from IP 197.251.45.123',
    ipAddress: '197.251.45.123',
    isResolved: false,
    escalated: false,
  },
  {
    id: 'inc_002',
    type: 'after_hours_access',
    severity: 'low',
    timestamp: new Date(Date.now() - 12 * 60 * 60 * 1000),
    userId: 'user2',
    userName: 'Akosua Mensah',
    resourceId: 'pf002',
    resourceName: 'PFile - Mary A. Owusu',
    description: 'Access to confidential record at 22:15 outside working hours',
    ipAddress: '192.168.1.45',
    isResolved: true,
    resolvedAt: new Date(Date.now() - 10 * 60 * 60 * 1000),
    resolvedBy: 'Security Admin',
    resolution: 'Verified with user - authorized overtime work',
    escalated: false,
  },
  {
    id: 'inc_003',
    type: 'bulk_download',
    severity: 'high',
    timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000),
    userId: 'user5',
    userName: 'Yaw Boateng',
    description: 'Downloaded 47 documents in 5 minutes - unusual pattern detected',
    ipAddress: '192.168.1.78',
    isResolved: false,
    escalated: true,
    escalatedTo: 'Department Head',
  },
  {
    id: 'inc_004',
    type: 'geofence_breach',
    severity: 'critical',
    timestamp: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000),
    userId: 'user1',
    userName: 'Kwame Asante',
    resourceId: 'doc_secret_001',
    resourceName: 'Cabinet Brief - Q4 Budget',
    description: 'Attempted access to top-secret document from outside geofenced area (Kumasi)',
    ipAddress: '41.215.176.45',
    location: { latitude: 6.6885, longitude: -1.6244, accuracy: 50, city: 'Kumasi', country: 'Ghana' },
    isResolved: true,
    resolvedAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000),
    resolvedBy: 'Security Administrator',
    resolution: 'Access blocked. User counseled on security protocols.',
    escalated: true,
    escalatedTo: 'Chief Security Officer',
  },
  {
    id: 'inc_005',
    type: 'privilege_escalation',
    severity: 'critical',
    timestamp: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000),
    userId: 'user_ext_001',
    userName: 'External Contractor',
    description: 'Attempted to access admin functions without authorization',
    ipAddress: '102.176.94.22',
    isResolved: true,
    resolvedAt: new Date(Date.now() - 4 * 24 * 60 * 60 * 1000),
    resolvedBy: 'IT Security',
    resolution: 'Contractor account suspended. Investigation completed - no data breach.',
    escalated: true,
    escalatedTo: 'CISO',
  },
];

export function SecurityAuditPage() {
  const [activeTab, setActiveTab] = useState<TabType>('audit');

  // Audit Trail state
  const [auditTransactions] = useState<AuditTransaction[]>(generateMockAuditTransactions);
  const [chainState] = useState<ChainState>(generateMockChainState);
  const [auditFilter, setAuditFilter] = useState<{
    type: AuditTransactionType | 'all';
    dateRange: 'today' | 'week' | 'month' | 'all';
    search: string;
  }>({ type: 'all', dateRange: 'all', search: '' });

  // Policies state
  const [policies] = useState<AccessPolicy[]>(generateMockPolicies);
  const [showPolicyModal, setShowPolicyModal] = useState(false);
  const [selectedPolicy, setSelectedPolicy] = useState<AccessPolicy | null>(null);

  // Incidents state
  const [incidents, setIncidents] = useState<SecurityIncident[]>(generateMockIncidents);
  const [incidentFilter, setIncidentFilter] = useState<{
    severity: 'all' | SecurityIncident['severity'];
    status: 'all' | 'resolved' | 'unresolved';
  }>({ severity: 'all', status: 'all' });
  const [showIncidentModal, setShowIncidentModal] = useState(false);
  const [selectedIncident, setSelectedIncident] = useState<SecurityIncident | null>(null);

  // Filtered audit transactions
  const filteredTransactions = useMemo(() => {
    return auditTransactions.filter(tx => {
      if (auditFilter.type !== 'all' && tx.type !== auditFilter.type) return false;
      if (auditFilter.search) {
        const search = auditFilter.search.toLowerCase();
        if (!tx.actor.name.toLowerCase().includes(search) &&
            !tx.subject.identifier.toLowerCase().includes(search) &&
            !tx.subject.name?.toLowerCase().includes(search)) {
          return false;
        }
      }
      if (auditFilter.dateRange !== 'all') {
        const txDate = new Date(tx.timestamp);
        const now = new Date();
        const daysDiff = Math.floor((now.getTime() - txDate.getTime()) / (1000 * 60 * 60 * 24));
        if (auditFilter.dateRange === 'today' && daysDiff > 0) return false;
        if (auditFilter.dateRange === 'week' && daysDiff > 7) return false;
        if (auditFilter.dateRange === 'month' && daysDiff > 30) return false;
      }
      return true;
    });
  }, [auditTransactions, auditFilter]);

  // Filtered incidents
  const filteredIncidents = useMemo(() => {
    return incidents.filter(inc => {
      if (incidentFilter.severity !== 'all' && inc.severity !== incidentFilter.severity) return false;
      if (incidentFilter.status === 'resolved' && !inc.isResolved) return false;
      if (incidentFilter.status === 'unresolved' && inc.isResolved) return false;
      return true;
    });
  }, [incidents, incidentFilter]);

  // Stats
  const auditStats = useMemo(() => {
    const today = auditTransactions.filter(tx => {
      const txDate = new Date(tx.timestamp);
      const now = new Date();
      return txDate.toDateString() === now.toDateString();
    });
    const failedAccess = auditTransactions.filter(tx => tx.type === 'failed_access');
    return {
      total: auditTransactions.length,
      today: today.length,
      failedAccess: failedAccess.length,
      chainValid: chainState.isValid,
    };
  }, [auditTransactions, chainState]);

  const incidentStats = useMemo(() => {
    const critical = incidents.filter(i => i.severity === 'critical' && !i.isResolved);
    const unresolved = incidents.filter(i => !i.isResolved);
    return {
      total: incidents.length,
      critical: critical.length,
      unresolved: unresolved.length,
      resolved: incidents.length - unresolved.length,
    };
  }, [incidents]);

  const handleResolveIncident = (incident: SecurityIncident) => {
    setSelectedIncident(incident);
    setShowIncidentModal(true);
  };

  const submitIncidentResolution = (resolution: string) => {
    if (!selectedIncident) return;
    setIncidents(prev => prev.map(inc =>
      inc.id === selectedIncident.id
        ? { ...inc, isResolved: true, resolvedAt: new Date(), resolvedBy: 'Current User', resolution }
        : inc
    ));
    setShowIncidentModal(false);
    setSelectedIncident(null);
  };

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getSeverityClass = (severity: SecurityIncident['severity']) => {
    const classes: Record<SecurityIncident['severity'], string> = {
      low: styles.severityLow,
      medium: styles.severityMedium,
      high: styles.severityHigh,
      critical: styles.severityCritical,
    };
    return classes[severity];
  };

  const getPolicyTypeClass = (type: PolicyType) => {
    const classes: Record<PolicyType, string> = {
      role_based: styles.policyRoleBased,
      attribute_based: styles.policyAttributeBased,
      context_based: styles.policyContextBased,
      temporary: styles.policyTemporary,
      emergency: styles.policyEmergency,
    };
    return classes[type];
  };

  const getTransactionTypeClass = (type: AuditTransactionType) => {
    if (type === 'failed_access') return styles.txFailed;
    if (type.includes('delete')) return styles.txDelete;
    if (type.includes('create') || type.includes('upload')) return styles.txCreate;
    if (type.includes('update') || type.includes('edit')) return styles.txUpdate;
    return styles.txView;
  };

  const tabs: { id: TabType; label: string; icon: string }[] = [
    { id: 'audit', label: 'Audit Trail', icon: '📋' },
    { id: 'policies', label: 'Access Policies', icon: '🔐' },
    { id: 'incidents', label: 'Security Incidents', icon: '🚨' },
  ];

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <div className={styles.headerContent}>
          <h1 className={styles.title}>Security & Audit</h1>
          <p className={styles.subtitle}>
            Blockchain-backed audit trail, access policies, and security monitoring
          </p>
        </div>
        <div className={styles.headerActions}>
          <button className={styles.exportBtn}>
            <span className={styles.btnIcon}>📤</span>
            Export Report
          </button>
          <button className={styles.verifyBtn}>
            <span className={styles.btnIcon}>✓</span>
            Verify Chain
          </button>
        </div>
      </header>

      {/* Chain Status Banner */}
      <div className={`${styles.chainBanner} ${chainState.isValid ? styles.chainValid : styles.chainInvalid}`}>
        <div className={styles.chainInfo}>
          <span className={styles.chainIcon}>{chainState.isValid ? '🔗' : '⚠️'}</span>
          <span className={styles.chainStatus}>
            Blockchain Status: <strong>{chainState.isValid ? 'Valid & Synced' : 'Verification Required'}</strong>
          </span>
        </div>
        <div className={styles.chainStats}>
          <span>Block #{chainState.currentBlockNumber}</span>
          <span className={styles.chainDivider}>|</span>
          <span>{chainState.totalTransactions.toLocaleString()} transactions</span>
          <span className={styles.chainDivider}>|</span>
          <span>{chainState.peers.length} peers connected</span>
        </div>
      </div>

      {/* Tabs */}
      <div className={styles.tabs}>
        {tabs.map(tab => (
          <button
            key={tab.id}
            className={`${styles.tab} ${activeTab === tab.id ? styles.activeTab : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            <span className={styles.tabIcon}>{tab.icon}</span>
            {tab.label}
            {tab.id === 'incidents' && incidentStats.unresolved > 0 && (
              <span className={styles.tabBadge}>{incidentStats.unresolved}</span>
            )}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className={styles.tabContent}>
        {/* Audit Trail Tab */}
        {activeTab === 'audit' && (
          <div className={styles.auditTab}>
            {/* Audit Stats */}
            <div className={styles.statsGrid}>
              <div className={styles.statCard}>
                <div className={styles.statValue}>{auditStats.total.toLocaleString()}</div>
                <div className={styles.statLabel}>Total Transactions</div>
              </div>
              <div className={styles.statCard}>
                <div className={styles.statValue}>{auditStats.today}</div>
                <div className={styles.statLabel}>Today's Activity</div>
              </div>
              <div className={styles.statCard}>
                <div className={`${styles.statValue} ${auditStats.failedAccess > 0 ? styles.statWarning : ''}`}>
                  {auditStats.failedAccess}
                </div>
                <div className={styles.statLabel}>Failed Access Attempts</div>
              </div>
              <div className={styles.statCard}>
                <div className={`${styles.statValue} ${styles.statSuccess}`}>
                  {auditStats.chainValid ? 'Valid' : 'Invalid'}
                </div>
                <div className={styles.statLabel}>Chain Integrity</div>
              </div>
            </div>

            {/* Filters */}
            <div className={styles.filters}>
              <div className={styles.filterGroup}>
                <label className={styles.filterLabel}>Transaction Type</label>
                <select
                  className={styles.filterSelect}
                  value={auditFilter.type}
                  onChange={e => setAuditFilter(prev => ({ ...prev, type: e.target.value as AuditTransactionType | 'all' }))}
                >
                  <option value="all">All Types</option>
                  <option value="pfile_view">PFile Viewed</option>
                  <option value="pfile_update">PFile Updated</option>
                  <option value="document_upload">Document Uploaded</option>
                  <option value="document_download">Document Downloaded</option>
                  <option value="checkout">File Checked Out</option>
                  <option value="checkin">File Checked In</option>
                  <option value="login">User Login</option>
                  <option value="logout">User Logout</option>
                  <option value="failed_access">Failed Access</option>
                </select>
              </div>
              <div className={styles.filterGroup}>
                <label className={styles.filterLabel}>Date Range</label>
                <select
                  className={styles.filterSelect}
                  value={auditFilter.dateRange}
                  onChange={e => setAuditFilter(prev => ({ ...prev, dateRange: e.target.value as typeof auditFilter.dateRange }))}
                >
                  <option value="all">All Time</option>
                  <option value="today">Today</option>
                  <option value="week">Last 7 Days</option>
                  <option value="month">Last 30 Days</option>
                </select>
              </div>
              <div className={styles.filterGroup}>
                <label className={styles.filterLabel}>Search</label>
                <input
                  type="text"
                  className={styles.filterInput}
                  placeholder="Search by user or record..."
                  value={auditFilter.search}
                  onChange={e => setAuditFilter(prev => ({ ...prev, search: e.target.value }))}
                />
              </div>
            </div>

            {/* Audit Table */}
            <div className={styles.tableContainer}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Timestamp</th>
                    <th>Type</th>
                    <th>User</th>
                    <th>MDA</th>
                    <th>Record</th>
                    <th>IP Address</th>
                    <th>Verified</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTransactions.slice(0, 25).map(tx => (
                    <tr key={tx.transactionId} className={getTransactionTypeClass(tx.type)}>
                      <td className={styles.timestamp}>{formatDate(tx.timestamp)}</td>
                      <td>
                        <span className={`${styles.txType} ${getTransactionTypeClass(tx.type)}`}>
                          {blockchainAudit.getTransactionTypeLabel(tx.type)}
                        </span>
                      </td>
                      <td>
                        <div className={styles.userCell}>
                          <span className={styles.userName}>{tx.actor.name}</span>
                          <span className={styles.userRole}>{tx.actor.role}</span>
                        </div>
                      </td>
                      <td className={styles.mda}>{tx.actor.mda}</td>
                      <td>
                        <div className={styles.recordCell}>
                          <span className={styles.recordId}>{tx.subject.identifier}</span>
                          {tx.subject.name && <span className={styles.recordName}>{tx.subject.name}</span>}
                        </div>
                      </td>
                      <td className={styles.ip}>{tx.ipAddress}</td>
                      <td>
                        <span className={`${styles.verifiedBadge} ${tx.verified ? styles.verified : styles.unverified}`}>
                          {tx.verified ? '✓' : '✗'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {filteredTransactions.length > 25 && (
              <div className={styles.tableFooter}>
                Showing 25 of {filteredTransactions.length} transactions
              </div>
            )}
          </div>
        )}

        {/* Access Policies Tab */}
        {activeTab === 'policies' && (
          <div className={styles.policiesTab}>
            <div className={styles.policiesHeader}>
              <h2 className={styles.sectionTitle}>Access Control Policies</h2>
              <button className={styles.addPolicyBtn} onClick={() => setShowPolicyModal(true)}>
                + New Policy
              </button>
            </div>

            <div className={styles.policiesGrid}>
              {policies.map(policy => (
                <div key={policy.id} className={`${styles.policyCard} ${!policy.isActive ? styles.policyInactive : ''}`}>
                  <div className={styles.policyHeader}>
                    <span className={`${styles.policyType} ${getPolicyTypeClass(policy.type)}`}>
                      {policy.type.replace('_', ' ')}
                    </span>
                    <span className={styles.policyPriority}>Priority: {policy.priority}</span>
                  </div>
                  <h3 className={styles.policyName}>{policy.name}</h3>
                  <p className={styles.policyDescription}>{policy.description}</p>

                  <div className={styles.policyConditions}>
                    <span className={styles.conditionsLabel}>Conditions:</span>
                    <div className={styles.conditionsList}>
                      {policy.conditions.map(c => (
                        <span key={c.id} className={styles.conditionTag}>
                          {c.type.replace('_', ' ')}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className={styles.policyScope}>
                    {policy.scope.sensitivityLevels && (
                      <div className={styles.scopeItem}>
                        <span className={styles.scopeLabel}>Sensitivity:</span>
                        <span className={styles.scopeValue}>
                          {policy.scope.sensitivityLevels.join(', ')}
                        </span>
                      </div>
                    )}
                  </div>

                  <div className={styles.policyActions}>
                    {policy.actions.map((action, i) => (
                      <span key={i} className={`${styles.actionBadge} ${styles[`action${action.type.charAt(0).toUpperCase() + action.type.slice(1)}`]}`}>
                        {action.type.replace('_', ' ')} ({action.scope})
                      </span>
                    ))}
                  </div>

                  <div className={styles.policyFooter}>
                    <span className={styles.policyMeta}>
                      Updated: {formatDate(policy.updatedAt)}
                    </span>
                    <div className={styles.policyBtns}>
                      <button
                        className={styles.policyEditBtn}
                        onClick={() => { setSelectedPolicy(policy); setShowPolicyModal(true); }}
                      >
                        Edit
                      </button>
                      <button className={`${styles.policyToggleBtn} ${policy.isActive ? styles.active : ''}`}>
                        {policy.isActive ? 'Active' : 'Inactive'}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Security Incidents Tab */}
        {activeTab === 'incidents' && (
          <div className={styles.incidentsTab}>
            {/* Incident Stats */}
            <div className={styles.statsGrid}>
              <div className={styles.statCard}>
                <div className={styles.statValue}>{incidentStats.total}</div>
                <div className={styles.statLabel}>Total Incidents</div>
              </div>
              <div className={styles.statCard}>
                <div className={`${styles.statValue} ${incidentStats.critical > 0 ? styles.statDanger : ''}`}>
                  {incidentStats.critical}
                </div>
                <div className={styles.statLabel}>Critical Unresolved</div>
              </div>
              <div className={styles.statCard}>
                <div className={`${styles.statValue} ${styles.statWarning}`}>
                  {incidentStats.unresolved}
                </div>
                <div className={styles.statLabel}>Pending Resolution</div>
              </div>
              <div className={styles.statCard}>
                <div className={`${styles.statValue} ${styles.statSuccess}`}>
                  {incidentStats.resolved}
                </div>
                <div className={styles.statLabel}>Resolved</div>
              </div>
            </div>

            {/* Filters */}
            <div className={styles.filters}>
              <div className={styles.filterGroup}>
                <label className={styles.filterLabel}>Severity</label>
                <select
                  className={styles.filterSelect}
                  value={incidentFilter.severity}
                  onChange={e => setIncidentFilter(prev => ({ ...prev, severity: e.target.value as typeof incidentFilter.severity }))}
                >
                  <option value="all">All Severities</option>
                  <option value="critical">Critical</option>
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                </select>
              </div>
              <div className={styles.filterGroup}>
                <label className={styles.filterLabel}>Status</label>
                <select
                  className={styles.filterSelect}
                  value={incidentFilter.status}
                  onChange={e => setIncidentFilter(prev => ({ ...prev, status: e.target.value as typeof incidentFilter.status }))}
                >
                  <option value="all">All Status</option>
                  <option value="unresolved">Unresolved</option>
                  <option value="resolved">Resolved</option>
                </select>
              </div>
            </div>

            {/* Incidents List */}
            <div className={styles.incidentsList}>
              {filteredIncidents.map(incident => (
                <div
                  key={incident.id}
                  className={`${styles.incidentCard} ${getSeverityClass(incident.severity)} ${incident.isResolved ? styles.resolved : ''}`}
                >
                  <div className={styles.incidentHeader}>
                    <div className={styles.incidentMeta}>
                      <span className={`${styles.severityBadge} ${getSeverityClass(incident.severity)}`}>
                        {incident.severity.toUpperCase()}
                      </span>
                      <span className={styles.incidentType}>
                        {accessControl.getIncidentTypeLabel(incident.type)}
                      </span>
                      {incident.escalated && (
                        <span className={styles.escalatedBadge}>
                          Escalated to {incident.escalatedTo}
                        </span>
                      )}
                    </div>
                    <span className={styles.incidentTime}>{formatDate(incident.timestamp)}</span>
                  </div>

                  <p className={styles.incidentDescription}>{incident.description}</p>

                  <div className={styles.incidentDetails}>
                    {incident.userName && (
                      <div className={styles.detailItem}>
                        <span className={styles.detailLabel}>User:</span>
                        <span className={styles.detailValue}>{incident.userName}</span>
                      </div>
                    )}
                    {incident.resourceName && (
                      <div className={styles.detailItem}>
                        <span className={styles.detailLabel}>Resource:</span>
                        <span className={styles.detailValue}>{incident.resourceName}</span>
                      </div>
                    )}
                    {incident.ipAddress && (
                      <div className={styles.detailItem}>
                        <span className={styles.detailLabel}>IP Address:</span>
                        <span className={styles.detailValue}>{incident.ipAddress}</span>
                      </div>
                    )}
                    {incident.location && (
                      <div className={styles.detailItem}>
                        <span className={styles.detailLabel}>Location:</span>
                        <span className={styles.detailValue}>
                          {incident.location.city}, {incident.location.country}
                        </span>
                      </div>
                    )}
                  </div>

                  {incident.isResolved ? (
                    <div className={styles.resolutionInfo}>
                      <div className={styles.resolutionHeader}>
                        <span className={styles.resolvedBadge}>Resolved</span>
                        <span className={styles.resolvedMeta}>
                          by {incident.resolvedBy} on {incident.resolvedAt && formatDate(incident.resolvedAt)}
                        </span>
                      </div>
                      <p className={styles.resolutionText}>{incident.resolution}</p>
                    </div>
                  ) : (
                    <div className={styles.incidentActions}>
                      <button
                        className={styles.resolveBtn}
                        onClick={() => handleResolveIncident(incident)}
                      >
                        Resolve Incident
                      </button>
                      {!incident.escalated && (
                        <button className={styles.escalateBtn}>
                          Escalate
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Policy Modal */}
      {showPolicyModal && (
        <div className={styles.modalOverlay} onClick={() => { setShowPolicyModal(false); setSelectedPolicy(null); }}>
          <div className={styles.modal} onClick={e => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2>{selectedPolicy ? 'Edit Policy' : 'Create New Policy'}</h2>
              <button className={styles.modalClose} onClick={() => { setShowPolicyModal(false); setSelectedPolicy(null); }}>
                &times;
              </button>
            </div>
            <div className={styles.modalBody}>
              <p className={styles.modalPlaceholder}>
                Policy configuration form would go here. This would include fields for:
              </p>
              <ul className={styles.modalList}>
                <li>Policy name and description</li>
                <li>Policy type (role-based, attribute-based, context-based, etc.)</li>
                <li>Priority level</li>
                <li>Scope (MDAs, sensitivity levels, record types)</li>
                <li>Conditions (time, location, authentication method, etc.)</li>
                <li>Actions (allow, deny, require MFA, etc.)</li>
                <li>Restrictions (watermark, prevent print, expiration)</li>
              </ul>
            </div>
            <div className={styles.modalFooter}>
              <button className={styles.cancelBtn} onClick={() => { setShowPolicyModal(false); setSelectedPolicy(null); }}>
                Cancel
              </button>
              <button className={styles.saveBtn}>
                {selectedPolicy ? 'Update Policy' : 'Create Policy'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Incident Resolution Modal */}
      {showIncidentModal && selectedIncident && (
        <div className={styles.modalOverlay} onClick={() => { setShowIncidentModal(false); setSelectedIncident(null); }}>
          <div className={styles.modal} onClick={e => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2>Resolve Incident</h2>
              <button className={styles.modalClose} onClick={() => { setShowIncidentModal(false); setSelectedIncident(null); }}>
                &times;
              </button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.incidentSummary}>
                <p><strong>Type:</strong> {accessControl.getIncidentTypeLabel(selectedIncident.type)}</p>
                <p><strong>Severity:</strong> {selectedIncident.severity}</p>
                <p><strong>Description:</strong> {selectedIncident.description}</p>
              </div>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Resolution Notes</label>
                <textarea
                  className={styles.formTextarea}
                  placeholder="Describe how this incident was resolved..."
                  rows={4}
                  id="resolution-notes"
                />
              </div>
            </div>
            <div className={styles.modalFooter}>
              <button className={styles.cancelBtn} onClick={() => { setShowIncidentModal(false); setSelectedIncident(null); }}>
                Cancel
              </button>
              <button
                className={styles.saveBtn}
                onClick={() => {
                  const textarea = document.getElementById('resolution-notes') as HTMLTextAreaElement;
                  submitIncidentResolution(textarea?.value || 'Resolved');
                }}
              >
                Mark as Resolved
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
